import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaShieldAlt, FaUser, FaEdit, FaCheck } from 'react-icons/fa';
import EditableContent from '../EditableContent.tsx';

interface SiteHeaderProps {
  variant: 'home' | 'catalog';
  isAuthenticated: boolean;
  isAdmin: boolean;
  user: { username: string } | null;
  isEditMode: boolean;
  dirtyFields: Set<string>;
  getContent: (key: string) => string;
  onContentSave: (contentId: string, value: string, contentType: 'text' | 'image') => Promise<void> | void;
  onDirtyStateChange: (contentId: string, isDirty: boolean) => void;
  onEditModeToggle: () => void;
  onLogout: () => void;
}

// Template header (brand + primary nav + burger menu) merged with the admin
// controls that used to live in EnteringNavbar (edit toggle, unsaved badge,
// login/logout). The Cognito flow replaces the template's login dialog stub.
const SiteHeader: React.FC<SiteHeaderProps> = ({
  variant,
  isAuthenticated,
  isAdmin,
  user,
  isEditMode,
  dirtyFields,
  getContent,
  onContentSave,
  onDirtyStateChange,
  onEditModeToggle,
  onLogout,
}) => {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const headerRef = useRef<HTMLElement>(null);
  const hashHref = (hash: string) => (variant === 'home' ? hash : `/${hash}`);

  useEffect(() => {
    if (!menuOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    const onClick = (e: MouseEvent) => {
      if (headerRef.current && !headerRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('click', onClick);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('click', onClick);
    };
  }, [menuOpen]);

  const closeMenu = () => setMenuOpen(false);

  return (
    <header ref={headerRef}>
      {isEditMode ? (
        <span className="brand">
          <EditableContent
            contentId="company-logo"
            contentType="image"
            defaultValue={getContent('company-logo')}
            isEditing={true}
            onSave={onContentSave}
            onDirtyStateChange={onDirtyStateChange}
            className="brand-logo"
          />
          <span className="brand-name">
            <EditableContent
              contentId="company-title"
              contentType="text"
              tag="span"
              defaultValue={getContent('company-title')}
              isEditing={true}
              onSave={onContentSave}
              onDirtyStateChange={onDirtyStateChange}
            />
            <EditableContent
              contentId="company-title-en"
              contentType="text"
              tag="small"
              defaultValue={getContent('company-title-en')}
              isEditing={true}
              onSave={onContentSave}
              onDirtyStateChange={onDirtyStateChange}
            />
          </span>
        </span>
      ) : (
        <a
          className="brand"
          href={hashHref('#home')}
          aria-label={`${getContent('company-title')} ${getContent('company-title-en')} 首頁`}
        >
          <img
            className="brand-logo"
            src={getContent('company-logo')}
            alt={`${getContent('company-title')} 標誌`}
            width={48}
            height={48}
          />
          <span className="brand-name">
            {getContent('company-title')}
            <small>{getContent('company-title-en')}</small>
          </span>
        </a>
      )}
      <nav id="primary-nav" className={menuOpen ? 'open' : undefined} aria-label="主要導覽">
        <a href={hashHref('#home')} onClick={closeMenu}>首頁</a>
        <a href={hashHref('#products')} onClick={closeMenu}>產品介紹</a>
        <a href={hashHref('#ordering')} onClick={closeMenu}>特規訂購</a>
        <a href={hashHref('#contact')} onClick={closeMenu}>
          聯絡資訊 <span>↗</span>
        </a>
        {isAdmin && (
          <>
            <span className="admin-indicator">
              <FaShieldAlt />
              管理員: {user?.username}
            </span>
            <div className="edit-toggle-wrapper">
              <button
                onClick={onEditModeToggle}
                className={`edit-toggle ${isEditMode ? 'active' : ''}`}
                title={isEditMode ? '完成編輯' : '編輯模式'}
                type="button"
              >
                {isEditMode ? <FaCheck /> : <FaEdit />}
                {isEditMode ? '完成編輯' : '編輯模式'}
              </button>
              {dirtyFields.size > 0 && isEditMode && (
                <span className="unsaved-badge">{dirtyFields.size}</span>
              )}
            </div>
          </>
        )}
        {isAuthenticated && !isAdmin && (
          <span className="user-greeting">
            <FaUser />
            {user?.username}
          </span>
        )}
        {isAuthenticated ? (
          <button className="login-trigger" type="button" onClick={onLogout}>
            登出
          </button>
        ) : (
          <button className="login-trigger" type="button" onClick={() => navigate('/login')}>
            登入
          </button>
        )}
      </nav>
      <button
        className="menu"
        aria-controls="primary-nav"
        aria-expanded={menuOpen}
        aria-label={menuOpen ? '收合選單' : '展開選單'}
        type="button"
        onClick={() => setMenuOpen(open => !open)}
      >
        選單 <span>＋</span>
      </button>
    </header>
  );
};

export default SiteHeader;
